import React, { useState, useEffect } from 'react';
import { Users, ExternalLink, Filter } from 'lucide-react';
import axios from 'axios';

const Sponsors = () => {
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchSponsors();
  }, []);

  const fetchSponsors = async () => {
    try {
      const response = await axios.get('/sponsors');
      setSponsors(response.data);
    } catch (error) {
      console.error('Error fetching sponsors:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(sponsors.map(s => s.category))];
  
  const filteredSponsors = sponsors.filter(sponsor => {
    if (selectedCategory === 'all') return true;
    return sponsor.category === selectedCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading sponsors...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Users className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Our Partners & Sponsors</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            We're proud to work with these fantastic local and national businesses 
            who support Crawley Athletic Football Club
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        {categories.length > 1 && (
          <div className="mb-8 flex items-center justify-center">
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-600" />
              <select
                className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-black focus:border-transparent"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>
                    {category === 'all' ? 'All Categories' : category}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Sponsors Grid */}
        {filteredSponsors.length === 0 ? (
          <div className="text-center py-16">
            <Users className="h-16 w-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No sponsors found</h3>
            <p className="text-gray-600 mb-8">
              {sponsors.length === 0 
                ? "We're currently building our partnership network. If you're interested in sponsoring Crawley Athletic, we'd love to hear from you!"
                : "No sponsors match the selected category."
              }
            </p>
            {sponsors.length === 0 && (
              <a 
                href="mailto:info@crawleyafc.uk?subject=Sponsorship Enquiry" 
                className="btn btn-primary"
              >
                Become a Sponsor
              </a>
            )}
          </div>
        ) : (
          <>
            <div className="sponsor-grid">
              {filteredSponsors.map((sponsor) => (
                <div key={sponsor.id} className="sponsor-card group">
                  <img
                    src={sponsor.logo_url}
                    alt={sponsor.name}
                    className="sponsor-logo"
                  />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {sponsor.name}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">{sponsor.category}</p>
                  
                  {sponsor.description && (
                    <p className="text-sm text-gray-500 mb-4 line-clamp-3">
                      {sponsor.description}
                    </p>
                  )}
                  
                  {sponsor.website_url && (
                    <a
                      href={sponsor.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-black hover:underline font-medium group-hover:text-gray-600 transition-colors"
                    >
                      Visit Website
                      <ExternalLink className="h-4 w-4 ml-1" />
                    </a>
                  )}
                </div>
              ))}
            </div>

            {/* Sponsorship Packages */}
            <div className="mt-16">
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-3xl font-bold text-center mb-8">Become a Partner</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-xl">1</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Community Impact</h3>
                    <p className="text-gray-600">
                      Support grassroots football and make a positive impact in the Crawley community
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-xl">2</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Brand Visibility</h3>
                    <p className="text-gray-600">
                      Get your brand seen at matches, on our website, and across our social media
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-white font-bold text-xl">3</span>
                    </div>
                    <h3 className="text-lg font-semibold mb-2">Networking</h3>
                    <p className="text-gray-600">
                      Connect with other local businesses and be part of our growing network
                    </p>
                  </div>
                </div>

                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-4">Interested in Sponsoring Crawley Athletic?</h3>
                  <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
                    We offer flexible sponsorship packages to suit businesses of all sizes. 
                    From kit sponsorship to ground advertising, there are many ways to support 
                    the club and promote your business.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="mailto:info@crawleyafc.uk?subject=Sponsorship Enquiry" 
                      className="btn btn-primary"
                    >
                      Contact Us About Sponsorship
                    </a>
                    <a 
                      href="/join-us" 
                      className="btn btn-secondary"
                    >
                      Learn More About Supporting Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Sponsors;